import React from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { Avatar, ProductLinkWithIcon } from 'components/common';
import { Icon } from 'components/common/icons';
import { PathStepProgressStatus } from 'components/paths';

import type {
  PathStep,
  PathStepProgress,
  ResolvedItemInPathStep
} from 'shared-types';

interface Props {
  progressData: PathStepProgress;
  steps: (Omit<PathStep, 'createdAt' | 'updatedAt'> & {
    items: ResolvedItemInPathStep[];
  })[];
}

const PathProgressTooltip = ({ progressData, steps }: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div className="steps">
      {steps?.map((step) => (
        <div className="step" key={step.id}>
          <div className="step-number">
            <Avatar backgroundColor={theme.lightGreyDark} fontSize="1.3rem">
              {step.number}
            </Avatar>
          </div>

          <div className="step-title">
            {step.items?.length > 1 ? (
              <div className="step-alternatives">
                <div className="step-alternatives-icon">
                  <Icon icon="share-alt" />
                </div>
                {getMessageWithPlaceholders({
                  message: messages.commonNumberOfAlternatives,
                  placeholders: { numberOfAlternatives: step.items.length }
                })}
              </div>
            ) : step.items?.[0] ? (
              <ProductLinkWithIcon
                disableLink={true}
                titleStyle={{
                  display: '-webkit-box',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1
                }}
                {...(step.items[0].type === 'achievement'
                  ? { achievement: step.items[0].achievement }
                  : step.items[0].type === 'course'
                  ? { course: step.items[0].course }
                  : step.items[0].type === 'event'
                  ? { calendarEvent: step.items[0].event }
                  : {})}
              />
            ) : (
              <div className="step-empty">{messages.commonNoContent}</div>
            )}
          </div>

          {progressData?.steps?.[step.id]?.completed ||
          progressData?.steps?.[step.id]?.started ? (
            <div className="step-progress">
              <PathStepProgressStatus
                iconOnly={true}
                id={`my-team-path-step-progress-${step.id}`}
                pathCompletion={progressData?.pathCompletion}
                progressData={progressData?.steps?.[step.id]}
              />
            </div>
          ) : null}
        </div>
      ))}

      <style jsx>{`
        .steps {
          flex-grow: 1;
          min-width: 280px;
          max-height: 175px;
          overflow-y: scroll;
        }
        .step {
          align-items: center;
          display: flex;
          gap: 15px;
          padding: 10px 15px;
        }
        .step:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .step-number {
          flex-grow: 0;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .step-title {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .step-empty {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-style: italic;
        }
        .step-alternatives {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .step-alternatives-icon {
          display: inline-block;
          margin-right: 8px;
          text-align: center;
          width: 24px;
        }
        .step-progress {
          flex-grow: 0;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default PathProgressTooltip;
